import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { decodeJwt } from 'jose';
import { AuthStateService } from '../services/auth-state.service';
import { AuthorizationService } from '../services/authorization.service';
import { LoginService } from '../services/login.service';
import { canUseFieldNotebook } from 'app/utils/field-notebook-access.utils';
import { canProvideRemoteSupport } from 'app/utils/remote-support-access.utils';

@Injectable()
export class AuthGuard implements CanActivate {
  private readonly loginService = inject(LoginService);
  private readonly authState = inject(AuthStateService);

  constructor(
    public router: Router,
    public translateService: TranslateService,
    public authorizationService: AuthorizationService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    const token = this.authState.getToken() ?? null;

    if (!token) {
      return this.loginService.silentRefresh().pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            this.router.navigate(['/login']);
            return of(false);
          }
          return of(this.checkAuthorization(route));
        }),
      );
    }

    try {
      const payload = decodeJwt(token);
      if (payload.exp !== undefined && payload.exp * 1000 < Date.now()) {
        return this.loginService.silentRefresh().pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              this.clearSessionAndRedirect();
              return of(false);
            }
            return of(this.checkAuthorization(route));
          }),
        );
      }
    } catch {
      this.clearSessionAndRedirect();
      return false;
    }

    return this.checkAuthorization(route);
  }

  private checkAuthorization(route: ActivatedRouteSnapshot): boolean {
    // Remote support opens for members of a support group, whatever their role.
    if (
      route.data?.requiresRemoteSupport &&
      !canProvideRemoteSupport(this.authState.getToken())
    ) {
      this.router.navigate(['/']);
      return false;
    }

    if (
      route.data?.requiresFieldNotebook &&
      !canUseFieldNotebook(this.authState.getUser()?.id)
    ) {
      this.router.navigate(['/']);
      return false;
    }

    if (!route.data?.authorization) {
      return true;
    }

    const auth = this.authorizationService.gethandler();
    const guard = route.data.authorization;

    if (auth.forbids(guard.permission, guard.resource)) {
      this.translateService
        .get('global.alert.users-premium-msg')
        .subscribe((msgOnlyPremium: string) => {
          this.router.navigate(['/'], { queryParams: { msg: msgOnlyPremium } });
        });
      return false;
    }

    return true;
  }

  private clearSessionAndRedirect(): void {
    this.authState.clearSession();
    this.router.navigate(['/login']);
  }
}
